// ---------------------------------------------------------------------------
// HeaderControlsBoundary — a page's topbar controls fail on their own (AL-2004)
// ---------------------------------------------------------------------------
// Pages inject their controls (the time bar, filters) into the shell topbar,
// so those render outside the route boundary: without this, one of them
// throwing took the whole app to the global fallback, sidebar included.
//
// The fallback is empty — the page below still works, only its controls are
// missing — and the error is reported. It resets itself when `resetKey` (the
// pathname) changes, so the next page gets its controls back. Not keyed on it:
// the first render under the new path still holds the previous page's
// controls, and a key would remount them there once.
// ---------------------------------------------------------------------------

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Sentry } from '@/config/sentry';

// Sentry passes its fallback only the error and resetError.
const ResetKeyContext = createContext<string | undefined>(undefined);

// Module-level, not an inline arrow: Sentry renders the fallback with
// createElement (see RouteErrorBoundary).
function NoControls({ resetError }: { readonly resetError: () => void }) {
  const resetKey = useContext(ResetKeyContext);
  const [failedAt] = useState(resetKey);

  useEffect(() => {
    if (resetKey !== failedAt) resetError();
  }, [resetKey, failedAt, resetError]);

  return <></>;
}

interface HeaderControlsBoundaryProps {
  readonly resetKey: string;
  readonly children: ReactNode;
}

export function HeaderControlsBoundary({ resetKey, children }: HeaderControlsBoundaryProps) {
  return (
    <ResetKeyContext.Provider value={resetKey}>
      <Sentry.ErrorBoundary
        beforeCapture={scope => scope.setTag('error_boundary', 'header_controls')}
        fallback={NoControls}
      >
        {children}
      </Sentry.ErrorBoundary>
    </ResetKeyContext.Provider>
  );
}
